import React, { createContext, useContext, useMemo } from 'react';
import { BEAUTY_PRODUCTS, beautyAppPath } from '../config/saasModules';
import { resolveExperienceHubPath } from '../../enterprise/salon/utils/salonHomeNav';

const BeautyProductContext = createContext(null);

/**
 * @param {{ product: import('../config/saasModules').BeautyProductConfig, enterprise?: boolean, children: React.ReactNode }} props
 */
export function BeautyProductProvider({
  product,
  enterprise = false,
  naturals = false,
  appBaseOverride,
  hubPathOverride,
  children,
}) {
  const value = useMemo(() => {
    const cfg = product || BEAUTY_PRODUCTS.makeup;
    if (appBaseOverride && hubPathOverride) {
      const appBase = String(appBaseOverride).replace(/\/$/, '');
      const hubPath = String(hubPathOverride);
      return {
        ...cfg,
        appBase,
        hubPath,
        lockedService: cfg.service,
        enterprise: false,
        naturals: false,
        appPath: (path) => beautyAppPath(appBase, path),
      };
    }
    let appBase;
    if (enterprise) {
      appBase = cfg.enterpriseAppBase || cfg.appBase;
    } else if (naturals) {
      appBase =
        cfg.naturalsAppBase ||
        (String(cfg.enterpriseAppBase || cfg.appBase || '').startsWith('/enterprise/')
          ? String(cfg.enterpriseAppBase || cfg.appBase).replace('/enterprise/', '/naturals/')
          : cfg.appBase);
    } else {
      appBase = cfg.appBase;
    }
    const liveHub = enterprise
      ? cfg.enterpriseHubPath || cfg.hubPath
      : naturals && cfg.naturalsHubPath
        ? cfg.naturalsHubPath
        : cfg.hubPath;
    const hubPath = resolveExperienceHubPath(liveHub);
    return {
      ...cfg,
      appBase,
      hubPath,
      lockedService: cfg.service,
      enterprise: Boolean(enterprise),
      appPath: (path) => beautyAppPath(appBase, path),
    };
  }, [product, enterprise, naturals, appBaseOverride, hubPathOverride]);

  return (
    <BeautyProductContext.Provider value={value}>
      {children}
    </BeautyProductContext.Provider>
  );
}

export function useBeautyProduct() {
  const ctx = useContext(BeautyProductContext);
  if (!ctx) {
    const cfg = BEAUTY_PRODUCTS.makeup;
    return {
      ...cfg,
      lockedService: cfg.service,
      enterprise: false,
      appPath: (path) => beautyAppPath(cfg.appBase, path),
    };
  }
  return ctx;
}
